<template>
  <cst-page-template :hide-menu="true">
    <fixed-bar class="fixed-bar">
      <price-bar></price-bar>
      <quote-navigation-bar @scroll-to="scrollTo"></quote-navigation-bar>
    </fixed-bar>
    <div class="container">
      <div class="customer-configuration-page__wrapper">
        <quote-status />
        <addendum-bar></addendum-bar>

        <div class="customer-configuration-page__header">
          <h1>{{ pageTitle }}</h1>
        </div>

        <modal
          v-if="(shouldCreateMatchingStockQuote && loading) || isKomNumberUpdating"
          id="stockmatch-quote-loading"
          :title="i18n.t('/stockmatch/loadingmodaltitle')"
        >
          <loader />
        </modal>

        <h3 class="section-header">{{ i18n.t('/quotepage/customerheader') }}</h3>

        <div class="customer-configuration-page__top">
          <div class="main">
            <add-customer />

            <add-company />
            <unlink-customer />
            <reference-leaseportal />

            <add-fleet-sales-contract ref="contractFleet" />

            <customer-type />

            <print-options />
          </div>

          <div class="sidebar">
            <order-info-block />
          </div>
        </div>

        <div v-show="hasOwner">
          <section>
            <images-block style="margin-bottom: 2em" />
          </section>

          <section>
            <div class="configuration-section--header section-header">
              <h3>{{ i18n.t('/quotepage/configurationheader') }}</h3>
              <div class="configuration-actions--wrapper">
                <configuration-code-container v-if="!isStockCar" :order-service="orderService" />
                <kom-number-container v-if="isStockCar" :order-service="orderService" />

                <stock-match-container v-if="isNewStockMatchingEnabled" ref="stockMatchContainer" />
                <legacy-stock-match-container
                  v-if="isLegacyStockMatchingEnabled"
                  ref="stockMatchContainer"
                />
              </div>
            </div>

            <configuration-summary :enable-no-bpm="true" :use-price-display-mode="true" />

            <div
              v-if="isNewStockMatchingEnabled"
              class="configuration-section--header section-header"
            >
              <h3>{{ stockProposalHeader }}</h3>
              <stock-proposal-actions
                v-if="!isStockCar"
                @open-modal="$refs.stockMatchContainer.toggleStockModal(true)"
              />
            </div>

            <stock-proposal-segment
              v-if="isNewStockMatchingEnabled"
              @open-modal="$refs.stockMatchContainer.toggleStockModal(true)"
              @stockcar-details="
                $refs.stockMatchContainer.setStockCarDetails($event.CommissionNumber, $event)
              "
              @reservation-status="$refs.stockMatchContainer.setReservationStatusId($event)"
              @select-car="$refs.stockMatchContainer.showMatchingStockCarToNewQuotePrompt($event)"
            />

            <h3 class="section-header">{{ i18n.t('/quotepage/extraheader') }}</h3>
            <control-codes ref="controlCodes" :order-service="orderService" />

            <dealer-accessory-catalog
              v-if="useNewAccessoryCatalog"
              ref="dealerOptions"
              class="customer-configuration-page__detail-costs"
            />
            <dealer-accessories
              v-else
              ref="dealerOptions"
              class="customer-configuration-page__detail-costs"
            />
            <delivery-costs class="customer-configuration-page__detail-costs" />
            <corrections class="customer-configuration-page__detail-costs" />

            <sub-total-price />

            <exchange-car-form />

            <settle :start-expanded="false" class="customer-configuration-page__detail-costs" />

            <h3 class="section-header">{{ i18n.t('/quotepage/financialservicesheader') }}</h3>
            <financial-services-block ref="financing" />

            <h3 class="section-header">{{ i18n.t('/quotepage/propositionheader') }}</h3>
            <proposition-block />
            <total-price-block :show-financial-overview-button="false" />

            <div
              v-show="isSalesOrder && (hasChildQuotes || hasLinkedChangeOrders)"
              class="additionalOrder"
            >
              <h3 class="section-header">{{ i18n.t('/quotepage/additionalorderheader') }}</h3>
              <additional-order-block
                :child-quotes="quote.ChildQuotes"
                :linked-change-orders="quote.LinkedChangeOrders"
              />
            </div>
          </section>
          <div class="quote-actions--wrapper">
            <quote-actions-and-progress
              :check-customer-type="true"
              :check-expire-date="true"
              :check-expected-delivery-date="true"
            />
          </div>

          <div class="quote-status--wrapper">
            <p v-if="orderError" class="quote-status-notice">
              {{ i18n.t('/QuotePage/OrderFailed', { error: orderError.error }) }}
            </p>
            <div v-if="canShowStatusHistory" class="status-history-sidebar">
              <h4>Status overzicht:</h4>
              <ul>
                <li v-for="(historyItem, idx) in statusHistory" :key="idx">
                  {{ formatDateTime(historyItem.UpdatedOn) }}
                  {{ formatStatusLine(historyItem) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <legacy-stock-match-single
        v-if="
          !dealerSettings.newStockMatchingEnabled &&
          dealerSettings.stockMatchingPopupEnabled &&
          !isStockCar
        "
      />
      <stock-match-single
        v-if="
          dealerSettings.newStockMatchingEnabled &&
          dealerSettings.stockMatchingPopupEnabled &&
          !isStockCar
        "
      />
    </div>
  </cst-page-template>
</template>

<script>
import quoteOrderService from '@/services/quoteOrderService.js'
import ConfigurationSummary from '@/components/generic/presentational/organisms/configurationSummary/ConfigurationSummary.vue'
import CustomerType from '@/components/generic/presentational/organisms/customerType/CustomerType.vue'
import PrintOptions from '@/components/generic/presentational/organisms/printOptions/PrintOptions.vue'
import AddCustomer from '@/components/generic/presentational/organisms/addCustomer/AddCustomer.vue'
import AddCompany from '@/components/generic/presentational/organisms/addCompany/AddCompany.vue'
import UnlinkCustomer from '@/components/generic/presentational/molecules/unlinkCustomer/UnlinkCustomer.vue'
import AddFleetSalesContract from '@/components/generic/presentational/organisms/addFleetSalesContract/AddFleetSalesContract.vue'
import TotalPriceBlock from '@/components/generic/presentational/organisms/totalPriceBlock/TotalPriceBlock.vue'
import ControlCodes from '@/components/generic/container/ControlCodes.vue'
import DealerAccessories from '@/components/generic/container/DealerAccessories.vue'
import DealerAccessoryCatalog from '@/components/generic/container/DealerAccessoryCatalog.vue'
import DeliveryCosts from '@/components/generic/container/DeliveryCosts.vue'
import Corrections from '@/components/generic/container/Corrections.vue'
import Settle from '@/components/generic/container/Settle.vue'
import QuoteActionsAndProgress from '@/components/generic/presentational/organisms/quoteActions/QuoteActionsAndProgress.vue'
import ConfigurationCodeContainer from '@/components/generic/container/ConfigurationCodeContainer.vue'
import KomNumberContainer from '@/components/generic/container/KomNumberContainer.vue'
import StockMatchContainer from '@/components/generic/container/StockMatchContainer.vue'
import LegacyStockMatchContainer from '@/components/generic/container/LegacyStockMatchContainer.vue'
import StockProposalSegment from '@/components/generic/container/StockProposalSegment.vue'
import StockProposalActions from '@/components/generic/container/StockProposalActions.vue'
import PropositionBlock from '@/components/generic/presentational/molecules/propositionBlock/PropositionBlock.vue'
import SubTotalPrice from '@/components/generic/presentational/molecules/SubTotalPrice/SubTotalPrice.vue'
import ImagesBlock from '@/components/generic/presentational/molecules/imagesBlock/ImagesBlock.vue'
import ExchangeCarForm from '@/components/generic/presentational/organisms/exchangeCarForm/ExchangeCarForm.vue'
import OrderInfoBlock from '@/components/generic/presentational/organisms/orderInfoBlock/OrderInfoBlock.vue'
import FinancialServicesBlock from '@/components/generic/container/FinancialServices/FinancialServicesBlock.vue'
import AdditionalOrderBlock from '@/components/generic/container/additionalOrderBlock/AdditionalOrderBlock.vue'

import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import ReferenceLeaseportal from '@/components/generic/presentational/organisms/referenceLeaseportal/ReferenceLeaseportal.vue'
import FixedBar from '@/components/generic/presentational/atoms/fixedBar/FixedBar.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import PriceBar from '@/components/generic/presentational/molecules/priceBar/PriceBar.vue'
import AddendumBar from '@/components/generic/presentational/molecules/addendumBar/AddendumBar.vue'
import QuoteNavigationBar from '@/components/generic/presentational/molecules/quoteNavigationBar/QuoteNavigationBar.vue'
import QuoteStatus from '@/components/generic/presentational/molecules/quoteStatus/quoteStatus.vue'
import StockMatchSingle from '@/components/generic/presentational/organisms/stockMatchSingle/StockMatchSingle.vue'
import LegacyStockMatchSingle from '@/components/generic/presentational/organisms/stockMatchSingle/LegacyStockMatchSingle.vue'

import { getStatus } from '@/helpers/statusHelper'
import CarOrders from '@/api/server/CarOrders.api'
import { ref } from 'vue'
import { mapState, mapGetters } from 'vuex'

export default {
  components: {
    DealerAccessoryCatalog,
    LegacyStockMatchContainer,
    KomNumberContainer,
    ConfigurationSummary,
    PrintOptions,
    AddCustomer,
    AddCompany,
    AddFleetSalesContract,
    ControlCodes,
    DealerAccessories,
    DeliveryCosts,
    SubTotalPrice,
    Corrections,
    CustomerType,
    ExchangeCarForm,
    Settle,
    OrderInfoBlock,
    CstPageTemplate,
    QuoteActionsAndProgress,
    TotalPriceBlock,
    PropositionBlock,
    ReferenceLeaseportal,
    ConfigurationCodeContainer,
    StockMatchContainer,
    StockProposalSegment,
    StockProposalActions,
    ImagesBlock,
    QuoteStatus,
    FinancialServicesBlock,
    FixedBar,
    PriceBar,
    AddendumBar,
    QuoteNavigationBar,
    AdditionalOrderBlock,
    Loader,
    Modal,
    UnlinkCustomer,
    StockMatchSingle,
    LegacyStockMatchSingle,
  },
  props: {
    configurationCode: {
      type: String,
      default: null,
    },
    stockCarId: {
      type: String,
      default: null,
    },
  },
  setup() {
    const contractFleet = ref(null)
    const controlCodes = ref(null)
    const dealerOptions = ref(null)
    const financing = ref(null)

    return {
      contractFleet,
      controlCodes,
      dealerOptions,
      financing,
    }
  },
  data() {
    return {
      loadingQuote: true,
      loadingSave: false,
      orderService: quoteOrderService,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      blocks: null,
      orderError: null,
    }
  },
  computed: {
    ...mapState('user', ['dealerSettings']),
    ...mapState('quote', ['shouldCreateMatchingStockQuote']),
    ...mapGetters({
      quote: 'quote/get',
      isStockCar: 'quote/isStockCar',
      isSalesOrder: 'quote/quoteStatusIsSalesOrder',
      hasOwner: 'quote/hasOwner',
      loading: 'quote/loading',
      isKomNumberUpdating: 'configuration/isKomNumberUpdating',
      useNewAccessoryCatalog: 'user/useNewAccessoryCatalog',
    }),
    ...mapState('configuration', ['configuration']),
    pageTitle() {
      return this.isSalesOrder
        ? this.i18n.t('/quotepage/salesordertitle')
        : this.i18n.t('/quotepage/title')
    },
    isLegacyStockMatchingEnabled() {
      return !this.dealerSettings.newStockMatchingEnabled && !this.isStockCar
    },
    isNewStockMatchingEnabled() {
      return this.dealerSettings.newStockMatchingEnabled && !this.isStockCar
    },
    stockProposalHeader() {
      return this.i18n.t('/quotepage/stockproposalheader', {
        modelName: this.configuration?.ModelName || '',
      })
    },
    // Everyone working on the quote sees where it is in the approval, not only the approvers.
    canShowStatusHistory() {
      return !!this.dealerSettings.salesApprovalEnabled && this.statusHistory.length > 0
    },
    establishmentId() {
      return this.$route.params.establishmentId
    },
    quoteId() {
      return this.$route.params.quoteId
    },
    newConfigurationCode() {
      // return from car configurator
      return this.$route.query.configuratie
    },
    statusLabel() {
      return getStatus(this.quote?.Status)
    },
    hasChildQuotes() {
      return this.quote?.ChildQuotes && this.quote?.ChildQuotes.length
    },
    hasLinkedChangeOrders() {
      return this.quote?.LinkedChangeOrders && this.quote?.LinkedChangeOrders.length
    },
    statusHistory() {
      return this.quote?.Metadata?.History || []
    },
  },
  watch: {
    // Only a PENDING quote waits for the car order job, so only then can ordering have failed.
    'quote.Status': {
      immediate: true,
      async handler(status) {
        this.orderError =
          status === 'PENDING'
            ? await CarOrders.getOrderError(this.quote.QuoteId).catch(() => null)
            : null
      },
    },
  },
  beforeUnmount() {
    this.$store.dispatch('quote/resetQuote')
    this.$store.dispatch('configuration/resetConfiguration')
    this.$store.dispatch('accessories/reset')
  },
  methods: {
    scrollTo(refName) {
      let ref = this[refName]?.$el
      if (ref?.length === 0) {
        ref = ref.nextElementSibling
      }
      if (ref) {
        window.scrollTo({
          top: ref.offsetTop - 150,
          behavior: 'smooth',
        })
      }
    },
    formatDateTime(timestamp) {
      if (!timestamp) return ''

      try {
        const date = new Date(timestamp)
        return date.toLocaleString('nl-NL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch (error) {
        console.error('Error formatting date:', error)
        return timestamp
      }
    },
    formatStatusLine(historyItem) {
      const status = historyItem.NewStatus
        ? getStatus(historyItem.NewStatus)
        : getStatus(historyItem.Status)

      if (historyItem.UpdatedBy) {
        return `${status} - ${historyItem.UpdatedBy}`
      }
      return status
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  @include center;
  display: grid;
  row-gap: 20px;
}

.customer-configuration {
  &-page__wrapper {
    max-width: 1440px;
    margin-top: 10px;
  }

  &-page__header {
    text-align: center;
  }

  &-page__top {
    display: flex;
    gap: 1.2em;

    > div.main {
      width: 66%;
    }
    > div.sidebar {
      width: 33%;
    }
  }
}

.configuration-actions--wrapper {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.quote-actions--wrapper {
  padding-top: 2rem;
}

.section-header {
  border-bottom: 1px solid #a3b8c0;
  align-items: center;
  padding-bottom: 0.625rem;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

h3 {
  font-size: 1.72122rem;
  color: $color-primary;
  margin-bottom: 0;
}

.configuration-section--header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.additionalOrder {
  margin-top: 30px;
}

.quote-status-notice {
  margin-top: 1.5rem;
  padding: 1rem;
  color: var(--ds-color-danger-text);
  background-color: var(--ds-color-danger-bg);
  border: 1px solid var(--ds-color-danger-border);
  border-radius: 4px;
}

.status-history-sidebar {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.status-history-sidebar h4 {
  font-size: 1.1rem;
  color: $color-primary;
  margin-bottom: 0.75rem;
  margin-top: 0;
  font-weight: 600;
}

.status-history-sidebar ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.status-history-sidebar li {
  margin-bottom: 0.25rem;
  font-size: 0.85rem;
  color: #333;
}

.status-history-sidebar li:last-child {
  margin-bottom: 0;
}
</style>
